import { CheckCircle, MoreHorizontal, RotateCcw, XCircle } from 'lucide-react';
import { useState } from 'react';

import type { ListHomeCareVendors } from '@virtual-care/client';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@virtual-care/shadcn';
import { ConfirmDialog, useAction } from '@virtual-care/ui';

import { ReactivationDialog } from './reactivation-dialog.tsx';
import { RejectionDialog } from './rejection-dialog.tsx';
import { SuspensionDialog } from './suspension-dialog.tsx';

export type HomeCareVendor = ListHomeCareVendors['records'][number];

export function getVendorDisplayName(vendor: HomeCareVendor): string {
  if (vendor.vendorType === 'INDIVIDUAL' && vendor.individualProfile) {
    const { firstName, parentName, lastName } = vendor.individualProfile;
    return (
      `${firstName ?? ''} ${parentName ?? ''} ${lastName ?? ''}`.trim() ||
      vendor.email
    );
  }
  if (vendor.vendorType === 'CLINIC' && vendor.clinicProfile) {
    return vendor.clinicProfile.clinicName ?? vendor.email;
  }
  return vendor.email;
}

export function VendorActionsCell({ vendor }: { vendor: HomeCareVendor }) {
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);

  const approveMutation = useAction('POST /homecare/{id}/activate', {
    invalidate: ['GET /homecare'],
  });

  const isPending = vendor.accountStatus === 'REGISTERED';
  const isActive = vendor.accountStatus === 'ACTIVE';
  const isSuspended = vendor.accountStatus === 'SUSPENDED';
  const displayName = getVendorDisplayName(vendor);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          {isPending && (
            <>
              <ConfirmDialog
                title="Approve Vendor"
                description={
                  <span>
                    Are you sure you want to approve{' '}
                    <strong>{displayName}</strong>?
                  </span>
                }
                confirmText="Approve"
                onConfirm={() => approveMutation.mutate({ id: vendor.id })}
              >
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  disabled={approveMutation.isPending}
                  className="text-green-600"
                >
                  <CheckCircle className="me-2 size-4" />
                  Approve Vendor
                </DropdownMenuItem>
              </ConfirmDialog>
              <DropdownMenuItem
                onClick={() => setRejectDialogOpen(true)}
                className="text-red-600"
              >
                <XCircle className="me-2 size-4" />
                Reject Vendor
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {isActive && (
            <>
              <DropdownMenuItem
                onClick={() => setSuspendDialogOpen(true)}
                className="text-amber-600"
              >
                <XCircle className="me-2 size-4" />
                Suspend Vendor
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {isSuspended && (
            <>
              <DropdownMenuItem
                onClick={() => setReactivateDialogOpen(true)}
                className="text-green-600"
              >
                <RotateCcw className="me-2 size-4" />
                Reactivate Vendor
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            onClick={() => navigator.clipboard.writeText(vendor.id)}
          >
            Copy vendor ID
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RejectionDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        vendorId={vendor.id}
        vendorName={displayName}
      />

      <SuspensionDialog
        open={suspendDialogOpen}
        onOpenChange={setSuspendDialogOpen}
        vendorId={vendor.id}
        vendorName={displayName}
      />

      <ReactivationDialog
        open={reactivateDialogOpen}
        onOpenChange={setReactivateDialogOpen}
        vendorId={vendor.id}
        vendorName={displayName}
      />
    </>
  );
}
